
import Vue from 'vue';
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import { WithMapFn } from './MapboxWrapper';
import { GeoJSONSourceRaw, Popup } from 'mapbox-gl';
import { LineString } from 'geojson';
import { LngLatDate } from './models';
import Settings from './RtPath/Settings';
import { formatDateTimeSeconds } from '../../util';

@Component({
  components: { Settings },
})
export default class RtPath extends Vue {
  @Inject('withMap')
  readonly withMap!: WithMapFn;
  @Prop({ required: true })
  readonly coordinates!: LngLatDate[];
  @Prop({ default: false })
  readonly visible!: boolean;

  @Prop({ required: true })
  readonly dateFrom!: Date;
  @Prop({ required: true })
  readonly dateTo!: Date;
  @Prop({ required: true })
  readonly dateCurrent!: Date;

  get sortedCoordinates(): LngLatDate[] {
    const ret = [...this.coordinates];
    ret.sort((l, r) => l.date.getTime() - r.date.getTime());
    return ret;
  }

  get currentPoint(): LngLatDate | null {
    const filtered = this.sortedCoordinates.filter(e => e.date.getTime() <= this.dateCurrent.getTime());
    if (!filtered) {
      return null;
    }
    return filtered[filtered.length - 1];
  }

  get currentPointDataSource(): GeoJSONSourceRaw {
    return {
      type: 'geojson',
      data: !this.currentPoint
          ? undefined
          : {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [this.currentPoint.lng, this.currentPoint.lat],
            },
            properties: {
              timeLabel: formatDateTimeSeconds(this.currentPoint.date),
            },
          },
    };
  }

  get fullPathDataSource(): GeoJSONSourceRaw {
    return {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          times: this.sortedCoordinates.map(e => e.date),
        },
        geometry: {
          type: 'LineString',
          coordinates: this.sortedCoordinates.map(e => [e.lng, e.lat]),
        } as LineString,
      },
    };
  }

  get partialPathDataSource(): GeoJSONSourceRaw {
    const filtered = this.sortedCoordinates.filter(e => e.date.getTime() <= this.dateCurrent.getTime());
    return {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          times: filtered.map(e => e.date),
        },
        geometry: {
          type: 'LineString',
          coordinates: filtered.map(e => [e.lng, e.lat]),
        } as LineString,
      },
    };
  }

  get pointsDataSource(): GeoJSONSourceRaw {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: this.sortedCoordinates.map(e => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [e.lng, e.lat],
          },
          properties: {
            timeLabel: formatDateTimeSeconds(e.date),
          },
        })),
      },
    };
  }

  get uid(): string {
    return this._uid;
  }

  mounted(): void {
    if (this.visible) {
      this.addPath();
    }
  }

  beforeDestroy(): void {
    if (this.visible) {
      this.removePath();
    }
  }

  @Watch('currentPointDataSource')
  private onCurrentPointDataSourceChanged(next: GeoJSONSourceRaw, prev: GeoJSONSourceRaw): void {
    if (next !== prev) {
      this.withMap(map => {
        const source = map.getSource('current' + this.uid) as mapboxgl.GeoJSONSource | undefined;
        if (source) {
          source.setData(next.data!);
        }
      });
    }
  }

  @Watch('partialPathDataSource')
  private onPartialPathDataSourceChanged(next: GeoJSONSourceRaw, prev: GeoJSONSourceRaw): void {
    if (next !== prev) {
      this.withMap(map => {
        const source = map.getSource('line' + this.uid) as mapboxgl.GeoJSONSource | undefined;
        if (source) {
          source.setData(next.data!);
        }
      });
    }
  }


  @Watch('pointsDataSource')
  private onPointsDataSourceChanged(next: GeoJSONSourceRaw, prev: GeoJSONSourceRaw): void {
    if (next !== prev) {
      this.withMap(map => {
        const source = map.getSource('points' + this.uid) as mapboxgl.GeoJSONSource | undefined;
        if (source) {
          source.setData(next.data!);
        }
      });
    }
  }

  @Watch('visible')
  private onVisibleChanged(next: boolean, prev: boolean): void {
    if (next !== prev) {
      if (next) {
        this.addPath();
      } else {
        this.removePath();
      }
    }
  }

  private addPath(): void {
    this.withMap(map => {
      map.addSource('line' + this.uid, this.partialPathDataSource);
      map.addSource('points' + this.uid, this.pointsDataSource);
      map.addSource('current' + this.uid, this.currentPointDataSource);

      const lineBaseWidth = 14;
      map.addLayer({
        id: 'line-outline-layer' + this.uid,
        type: 'line',
        source: 'line' + this.uid,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#111827', 'line-width': lineBaseWidth, 'line-opacity': 0.6 },
      } as mapboxgl.LineLayer);

      map.addLayer({
        id: 'points-circle-layer' + this.uid,
        type: 'circle',
        source: 'points' + this.uid,
        paint: {
          'circle-radius': 4,
          'circle-color': '#1976d2',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
        },
      } as mapboxgl.CircleLayer);

      map.addLayer({
        id: 'current-circle-layer' + this.uid,
        type: 'circle',
        source: 'current' + this.uid,
        paint: {
          'circle-radius': 4,
          'circle-color': '#ffcc00',
          'circle-stroke-color': '#111',
          'circle-stroke-width': 1,
        },
      } as mapboxgl.CircleLayer);

      const popup = new Popup({ closeButton: false, closeOnClick: false });
      map.on('mouseenter', 'points-circle-layer' + this.uid, (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const feature = e.features && e.features[0];
        if (!feature) {
          return;
        }
        const coords = (feature.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
        const propsAny = feature.properties as { timeLabel: string };
        const html = `<strong>${propsAny?.timeLabel}</strong>`;
        popup!.setLngLat(coords).setHTML(html).addTo(map);
      });
      map.on('mouseleave', 'points-circle-layer' + this.uid, () => {
        map.getCanvas().style.cursor = '';
        if (popup) {
          popup.remove();
        }
      });
    });
  }

  private removePath(): void {
    this.withMap(map => {
      map.removeLayer('current-circle-layer' + this.uid);
      map.removeLayer('points-circle-layer' + this.uid);
      map.removeLayer('line-outline-layer' + this.uid);
      map.removeSource('current' + this.uid);
      map.removeSource('points' + this.uid);
      map.removeSource('line' + this.uid);
    });
  };
}
